<template>
	<Header
		title="Email downloads"
		:is-loading="loaderEnabled && listState.isLoading.value">
		<Button
			v-if="appId"
			icon="fal fa-xmark"
			label="All apps"
			outlined
			severity="secondary"
			@click="showAllApps" />
	</Header>

	<Container>
		<TableFilters
			placeholder="Search by app name..."
			:controls
			:filters
			@updated="handleFilterUpdate" />
	</Container>

	<Container class="email-downloads">
		<Message
			v-if="error"
			severity="error"
			size="small">
			{{ error }}
		</Message>
		<ApiTable
			v-model:expanded-rows="expandedRows"
			clickable
			data-key="id"
			:list-state
			:loading="false"
			@row-click="toggleExpand">
			<Column
				expander
				:style="{ width: '20px' }" />
			<Column header="App">
				<template #body="{ data }">
					<div class="email-downloads__stack">
						<router-link
							v-if="data.app_id"
							class="email-downloads__app-name"
							:to="{ name: 'apps-details', params: { id: data.app_id }, query: { tab: 'admin' } }">
							{{ data.app_name }}
						</router-link>
						<span
							v-else
							class="email-downloads__app-name">
							{{ data.app_name }}
						</span>
						<span class="email-downloads__id">
							<template v-if="data.build_version !== null">v{{ data.build_version }} · </template
							>#{{ data.id }}
						</span>
					</div>
				</template>
			</Column>
			<Column header="Recipient">
				<template #body="{ data }">
					<div class="email-downloads__stack">
						<span>{{ data.recipient }}</span>
						<span class="email-downloads__muted">
							by {{ data.sender?.email ?? 'deleted admin' }}
						</span>
					</div>
				</template>
			</Column>
			<Column header="Status">
				<template #body="{ data }">
					<EmailDownloadStatus :download="data" />
				</template>
			</Column>
			<Column header="Link">
				<template #body="{ data }">
					<span
						v-if="['pending', 'revoked'].includes(data.download_status)"
						class="email-downloads__muted">
						–
					</span>
					<div
						v-else
						class="email-downloads__stack">
						<Tag
							:severity="linkSeverity(data)"
							:value="linkLabels[data.download_status]" />
						<span
							v-if="data.download_status === 'available' && data.expires_at"
							class="email-downloads__muted">
							Expires {{ format(new Date(data.expires_at), 'PP') }}
						</span>
						<span
							v-else-if="['claimed', 'used'].includes(data.download_status)"
							class="email-downloads__muted">
							Claimed by {{ data.claimed_by_email }}
						</span>
					</div>
				</template>
			</Column>
			<Column header="Requested">
				<template #body="{ data }">
					<span class="email-downloads__date">{{ formatHuman(data.created_at) }}</span>
				</template>
			</Column>
			<Column>
				<template #body="{ data }">
					<div class="email-downloads__actions">
						<Button
							v-if="data.status === 'failed'"
							v-tooltip.top="'Retry'"
							aria-label="Retry"
							icon="fal fa-rotate-right"
							rounded
							text
							:disabled="busy"
							@click.stop="act(() => listState.api.retry(data.id))" />
						<Button
							v-if="['sent', 'failed', 'revoked'].includes(data.status) && !data.replacement"
							v-tooltip.top="'Send again'"
							aria-label="Send again"
							icon="fal fa-paper-plane"
							rounded
							text
							:disabled="busy"
							@click.stop="confirmResend(data)" />
						<Button
							v-if="!data.revoked_at"
							v-tooltip.top="'Revoke'"
							aria-label="Revoke"
							icon="fal fa-ban"
							rounded
							severity="danger"
							text
							:disabled="busy"
							@click.stop="confirmRevoke(data)" />
					</div>
				</template>
			</Column>
			<template #expansion="{ data }">
				<div class="email-downloads__details">
					<div class="email-downloads__email">
						<strong>{{ data.subject }}</strong>
						<p class="email-downloads__message">{{ data.message }}</p>
					</div>
					<dl class="email-downloads__facts">
						<template
							v-for="[label, value] in facts(data)"
							:key="label">
							<dt>{{ label }}</dt>
							<dd :class="{ 'email-downloads__fact--danger': label === 'Failure' }">
								{{ value }}
							</dd>
						</template>
					</dl>
				</div>
			</template>
		</ApiTable>
	</Container>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useConfirm } from 'primevue/useconfirm'
import { format } from 'date-fns'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Tag from 'primevue/tag'
import Header from '@/components/Header.vue'
import Container from '@/components/Container.vue'
import TableFilters from '@/components/filters/TableFilters.vue'
import EmailDownloadStatus from './components/EmailDownloadStatus.vue'
import useApiTable from '@/components/Table/useApiTable'
import QueryBuilder from '@/helpers/models/QueryBuilder'
import { FilterControls } from '@/helpers/filters/FilterControls'
import { FilterControl } from '@/helpers/filters/FilterControl'
import { Filters } from '@/helpers/filters/Filters'
import type { EmailDownload, EmailDownloadModel } from '@/models/EmailDownload/Model'
import { useEmailDownloadListState } from '@/models/EmailDownload/States'
import { emailDownloadError } from '@/models/EmailDownload/Errors'

const inProgressStatuses = ['queued', 'generating', 'sending']
// Pending and revoked links show a dash instead: the Status column already covers revocation.
const linkLabels: Partial<Record<EmailDownload['download_status'], string>> = {
	available: 'Available',
	claimed: 'Claimed',
	used: 'Used',
	expired: 'Expired',
}

const listState = useEmailDownloadListState()
const { ApiTable, Column } = useApiTable(listState)
const route = useRoute()
const router = useRouter()
const confirm = useConfirm()
const expandedRows = ref<Record<string, boolean>>({})
const loaderEnabled = ref(true)
const error = ref('')
const busy = ref(false)
const appId = computed(() =>
	typeof route.query.app_id === 'string' ? route.query.app_id : undefined,
)

const controls = new FilterControls([
	new FilterControl<EmailDownloadModel>({
		key: 'in-progress',
		name: 'In progress',
		icon: 'fal fa-spinner',
		queryUpdateHandler(qb) {
			return this.operator === 'is'
				? qb.whereIn('status', inProgressStatuses)
				: qb.whereNotIn('status', inProgressStatuses)
		},
	}),
	...[
		{ key: 'sent', name: 'Sent', icon: 'fal fa-check' },
		{ key: 'failed', name: 'Failed', icon: 'fal fa-exclamation-triangle' },
		{ key: 'revoked', name: 'Revoked', icon: 'fal fa-ban' },
	].map(
		(status) =>
			new FilterControl<EmailDownloadModel>({
				...status,
				queryUpdateHandler(qb) {
					return qb.where('status', this.operator === 'is' ? '=' : '!=', status.key)
				},
			}),
	),
])
const filters = ref(Filters.fromQuery(route.query as any, controls))

// The URL holds the search, filters and app; every change reloads the first page.
watch(
	() => route.query,
	() => {
		filters.value = Filters.fromQuery(route.query as any, controls)
		const appQuery = new QueryBuilder<EmailDownloadModel>()
		if (appId.value) appQuery.where('app_id', '=', appId.value)
		listState.defaultParams = {
			orderBy: 'id',
			order: 'desc',
			per_page: 20,
			with: ['replacement', 'sender'],
			search: filters.value.search || undefined,
			filters: [filters.value.getFilter(), appQuery.getFilter()],
		}
		loaderEnabled.value = true
		void refresh({ page: 1 })
	},
	{ immediate: true },
)

watchEffect(() => {
	if (listState.isLoaded.value && !listState.isLoading.value) loaderEnabled.value = false
})

// Poll only while a row on this page is still being generated or sent. A tick never interrupts a
// pending request, since ListState aborts it: a slow refresh, or a page or filter change.
let pollInterval: ReturnType<typeof setInterval> | undefined
const pollingNeeded = computed(() =>
	listState.list.value.some((download) => inProgressStatuses.includes(download.status)),
)
watch(pollingNeeded, (needed) => {
	clearInterval(pollInterval)
	if (needed) {
		pollInterval = setInterval(() => {
			if (!listState.isLoading.value) void refresh()
		}, 2000)
	}
})
onUnmounted(() => {
	clearInterval(pollInterval)
	listState.requestController.abort()
})

async function refresh(params?: { page: number }) {
	try {
		await listState.getList(params)
		error.value = ''
	} catch (exception) {
		error.value = await emailDownloadError(exception)
	}
}

function handleFilterUpdate() {
	router.replace({ query: { ...filters.value.toQueryObject(), app_id: appId.value } })
}

function showAllApps() {
	router.replace({ query: filters.value.toQueryObject() })
}

function formatHuman(date: string) {
	return format(new Date(date), 'PPpp')
}

// Expanded rows are keyed by id, so they stay open while polling replaces the rows.
function toggleExpand(event: { data: EmailDownload }) {
	const { [event.data.id]: expanded, ...rest } = expandedRows.value
	expandedRows.value = expanded ? rest : { ...rest, [event.data.id]: true }
}

function linkSeverity(download: EmailDownload) {
	if (download.download_status === 'available') return 'success'
	if (download.download_status === 'claimed') return 'info'
	return 'secondary'
}

function facts(download: EmailDownload) {
	const date = (value: string | null) => value && formatHuman(value)
	return (
		[
			['Email accepted', date(download.sent_at)],
			['Expires', date(download.expires_at)],
			[
				'Claimed by',
				download.claimed_by_email &&
					[download.claimed_by_email, date(download.claimed_at)].filter(Boolean).join(' · '),
			],
			['Retry until', date(download.retry_until)],
			['Last download', date(download.last_download_at)],
			['Downloads started', download.download_attempts || null],
			['Send attempts', download.attempt_count || null],
			['Revoked', date(download.revoked_at)],
			['Replaces', download.replaces_id && `#${download.replaces_id}`],
			['Replaced by', download.replacement && `#${download.replacement.id}`],
			['Failure', download.failure_reason],
		] as const
	).filter(([, value]) => value)
}

function confirmResend(download: EmailDownload) {
	confirm.require({
		header: 'Send a new link?',
		message: `${download.recipient} gets a new link. The current link stops working.`,
		rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
		acceptProps: { label: 'Send' },
		accept: () => act(() => listState.api.resend(download.id)),
	})
}

function confirmRevoke(download: EmailDownload) {
	confirm.require({
		header: 'Revoke this link?',
		message: "The link stops working right away. A download already in progress can't be stopped.",
		icon: 'fas fa-exclamation-triangle',
		rejectProps: { label: 'Cancel', severity: 'secondary', outlined: true },
		acceptProps: { label: 'Revoke', severity: 'danger' },
		accept: () => act(() => listState.api.revoke(download.id)),
	})
}

async function act(request: () => Promise<unknown>) {
	if (busy.value) return
	busy.value = true
	try {
		await request()
		await refresh()
	} catch (exception) {
		error.value = await emailDownloadError(exception)
	} finally {
		busy.value = false
	}
}
</script>

<style scoped lang="scss">
.email-downloads {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding-top: 0;

	.email-downloads__stack {
		display: flex;
		flex-direction: column;
		align-items: start;
		gap: 4px;
	}

	.email-downloads__app-name {
		font-weight: 600;
		color: inherit;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}

	.email-downloads__id {
		font-size: 11px;
		opacity: 0.8;
		font-family: monospace;
	}

	.email-downloads__muted {
		font-size: 13px;
		color: var(--p-text-muted-color);
	}

	.email-downloads__date {
		white-space: nowrap;
	}

	.email-downloads__actions {
		display: flex;
		justify-content: flex-end;
		column-gap: 8px;
	}

	.email-downloads__details {
		display: grid;
		grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
		gap: 24px;

		@media (max-width: 768px) {
			grid-template-columns: minmax(0, 1fr);
		}

		.email-downloads__email {
			display: flex;
			flex-direction: column;
			gap: 8px;

			.email-downloads__message {
				margin: 0;
				white-space: pre-wrap;
				overflow-wrap: anywhere;
				color: var(--p-text-muted-color);
			}
		}

		.email-downloads__facts {
			display: grid;
			grid-template-columns: max-content minmax(0, 1fr);
			align-content: start;
			gap: 8px 16px;
			margin: 0;
			font-size: 13px;

			dt {
				color: var(--p-text-muted-color);
			}

			dd {
				margin: 0;
				overflow-wrap: anywhere;
			}

			.email-downloads__fact--danger {
				color: var(--p-red-400);
			}
		}
	}
}
</style>
